function sortByValue(value: unknown[] | Map<string, unknown> | Set<unknown>) {
  if (Array.isArray(value)) {
    return value.toSorted();
  } else if (value instanceof Map) {
    return Array.from(value.entries())
      .sort()
      .map(([k, v]) => [k, Array.isArray(v) ? v.toSorted() : v]);
  } else if (value instanceof Set) {
    return Array.from(value).sort();
  }

  return value;
}

/**
 * Stabilizes a query key by sorting arrays, maps, sets, and object values to ensure consistent caching.
 * This is useful for React Query to prevent unnecessary refetches when the same data is represented differently.
 * Note: Sorting is performed at the first level of nesting for objects and arrays, and for arrays within Map values.
 * Other nested structures are not sorted.
 * The function modifies the input array in place.
 *
 * @param queryKey - query key segments that can be:
 *   - string: Direct key segments
 *   - undefined: Optional segments
 *   - Record<string, unknown>: Objects with string keys
 *   - unknown[]: Arrays of any type
 *   - Map<string, unknown>: Maps with string keys
 *   - Set<unknown>: Sets of any type
 * @returns array with arrays, maps, sets, and object values sorted by value
 *
 * @example
 * stableQueryKey(['users', [3, 1, 2]]) // ['users', [1, 2, 3]]
 *
 * @example
 * stableQueryKey(['users', new Map([['b', 2], ['a', 1]])]) // ['users', [['a', 1], ['b', 2]]]
 *
 * @example
 * stableQueryKey(['users', new Set([3, 1, 2])]) // ['users', [1, 2, 3]]
 */
export function stableQueryKey(
  ...queryKey: Array<
    string | undefined | Record<string, unknown> | unknown[] | Map<string, unknown> | Set<unknown>
  >
) {
  for (let i = 0; i < queryKey.length; i++) {
    const keySegment = queryKey[i];

    if (Array.isArray(keySegment) || keySegment instanceof Map || keySegment instanceof Set) {
      queryKey[i] = sortByValue(keySegment);
    } else if (keySegment != null && typeof keySegment === 'object') {
      const copy = { ...keySegment };
      queryKey[i] = copy;

      for (const [k, value] of Object.entries(copy)) {
        if (Array.isArray(value) || value instanceof Map || value instanceof Set) {
          copy[k] = sortByValue(value);
        } else {
          copy[k] = value;
        }
      }
    }
  }

  return queryKey;
}
